import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoForm = ({ country, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    solution: 'CRM',
    challenge: '',
    preferred_date: '',
    additional_message: '',
    consent: false
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/demo`, {
        ...formData,
        country: country || 'br'
      });
      
      setStatus({
        type: 'success',
        message: 'Solicitação enviada com sucesso! Entraremos em contato em breve.'
      });
      
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao enviar solicitação. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
      {status.message && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          status.type === 'success' ? 'bg-aug-green-light text-aug-green' : 'bg-red-100 text-aug-error'
        }`}>
          {status.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{status.message}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-aug-gray-dark mb-1">
            Nome Completo *
          </label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
            data-testid="demo-full-name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-aug-gray-dark mb-1">
            Email *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
            data-testid="demo-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-aug-gray-dark mb-1">
            Telefone *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
            data-testid="demo-phone"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-aug-gray-dark mb-1">
            Empresa *
          </label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
            data-testid="demo-company"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-aug-gray-dark mb-1">
          Cargo
        </label>
        <input
          type="text"
          name="position"
          value={formData.position}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-aug-gray-dark mb-1">
          Solução de Interesse *
        </label>
        <select
          name="solution"
          value={formData.solution}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
          data-testid="demo-solution"
        >
          <option value="CRM">CRM Kommo</option>
          <option value="IA">Agentes de IA</option>
          <option value="Both">Ambas</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-aug-gray-dark mb-1">
          Principal Desafio *
        </label>
        <textarea
          name="challenge"
          value={formData.challenge}
          onChange={handleChange}
          required
          rows={3}
          className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
          data-testid="demo-challenge"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-aug-gray-dark mb-1">
          Data Preferencial *
        </label>
        <input
          type="date"
          name="preferred_date"
          value={formData.preferred_date}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
          data-testid="demo-date"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-aug-gray-dark mb-1">
          Mensagem Adicional
        </label>
        <textarea
          name="additional_message"
          value={formData.additional_message}
          onChange={handleChange}
          rows={2}
          className="w-full px-4 py-2 border border-aug-sand rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
        />
      </div>

      <div className="flex items-start space-x-2">
        <input
          type="checkbox"
          name="consent"
          checked={formData.consent}
          onChange={handleChange}
          required
          className="mt-1"
          data-testid="demo-consent"
        />
        <label className="text-sm text-aug-gray-medium">
          Concordo em receber comunicações da Agro Urban Global *
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-aug-blue hover:bg-aug-blue-dark text-white py-3 rounded-lg font-semibold transition-colors duration-300 disabled:opacity-50"
        data-testid="demo-submit"
      >
        {loading ? 'Enviando...' : 'Solicitar Demonstração'}
      </button>
    </form>
  );
};

export default DemoForm;